"use client";

import { useQuery } from "@apollo/client";
import UserAvatar from "components/ui/v2/user-avatar-compat-v2";
import Last7DaysPercent from "components/ui/v2/home/bundle/Dashboard/Organization/Last7DaysPercent";
import TeamLineChart from "components/ui/v2/home/bundle/Dashboard/Organization/TeamLineChart";
import { LiquidPill } from "components/ui/v2";
import { gqlDashboard } from "gql";
import { ITeamRank } from "gql/Dashboard/queries";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import { UnmountClosed } from "react-collapse";
import { ProgressRing } from "components/ui/v2/composites/progress-ring";
import { Swiper, SwiperSlide } from "swiper/react";
import { percentColor } from "utils/string";
import { HomeLoadingStateV2 } from "./HomeLoadingStateV2";

function TeamRankingCardV2({ data }: { data: ITeamRank }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);

  const isLeader = data.users.filter((user) => user.isLeader);
  const members = data.users.filter((user) => !user.isLeader);
  const usersWithLeader = [...isLeader, ...members];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/70 bg-white dark:border-zinc-800 dark:bg-zinc-900/50">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full p-4 text-left transition-colors hover:bg-slate-50/80 dark:hover:bg-zinc-800/50"
      >
        <p className="mb-3 text-sm font-semibold text-slate-900 dark:text-zinc-100">
          {data.teamName}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-medium text-slate-500 dark:text-zinc-400">
                {t("Líderes")}
              </p>
              <div className="flex">
                {data.leaders.map((user) => (
                  <div className="-ml-1 first:ml-0" key={`${user.name}${user.lastname}`}>
                    <UserAvatar
                      user={{ ...user, profileImage: user.avatar }}
                      size={23}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium text-slate-500 dark:text-zinc-400">
                {t("Nº de miembros")}
              </p>
              <div className="flex">
                {data.users.slice(0, 4).map((user) => (
                  <div
                    className="-ml-1 first:ml-0"
                    key={`${user.name}${user.lastname}${user.roleName}`}
                  >
                    <UserAvatar
                      user={{ ...user, profileImage: user.avatar }}
                      size={23}
                    />
                  </div>
                ))}
                {data.users.length > 4 && (
                  <div className="-ml-1">
                    <UserAvatar
                      user={{
                        name: "+",
                        lastname: `${data.users.length - 4}`
                      }}
                      size={23}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500 dark:text-zinc-400">
                  {t("Últimos 7 días")}
                </p>
                <Last7DaysPercent last7DaysPercent={data.last7DaysPercent} />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500 dark:text-zinc-400">
                  {t("Últimos 30 días")}
                </p>
                <Last7DaysPercent last7DaysPercent={data.last30DaysPercent} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <TeamLineChart
                team={data.team}
                last7DaysPercent={data.last12MonthsPercent}
              />
            </div>
            <div className="flex shrink-0 justify-center">
              <ProgressRing
                value={data.teamOverallPerformance}
                size={56}
                strokeColor={percentColor(data.teamOverallPerformance)}
                showValue
              />
            </div>
          </div>
        </div>
      </button>

      <UnmountClosed isOpened={open}>
        <div className="border-t border-slate-200/70 dark:border-zinc-800">
          <div className="grid grid-cols-3 bg-slate-50 px-3 py-2 text-center text-xs font-semibold text-slate-700 dark:bg-zinc-900 dark:text-zinc-200">
            <span>{t("Miembros del equipo")}</span>
            <span>{t("Nivel de madurez")}</span>
            <span>{t("Rol")}</span>
          </div>
          <Swiper
            direction="vertical"
            scrollbar={{ draggable: true }}
            spaceBetween={10}
            slidesPerView={5}
            pagination
            className="h-[300px] max-h-[300px] pt-2.5"
          >
            {usersWithLeader.map((user) => (
              <SwiperSlide key={`${user.name}${user.lastname}${user.roleName}`}>
                <div className="grid h-[30px] grid-cols-3 items-center px-3">
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      size={30}
                      user={{ ...user, profileImage: user.avatar }}
                    />
                    <span className="truncate text-sm">
                      {user.name} {user.lastname}
                    </span>
                    {user.isLeader && (
                      <LiquidPill
                        variant="indigo"
                        appearance="tinted"
                        size="xs"
                        text={t("Líder")}
                      />
                    )}
                  </div>
                  <span className="text-center text-sm">{user.maturityLevel}</span>
                  <span className="text-center text-sm">{user.roleName}</span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </UnmountClosed>
    </div>
  );
}

export function DashboardOrganizationRankingPanelV2() {
  const { data: organizationalData, loading: loadingRanking } = useQuery<{
    organizationalRanking: { teamRank: ITeamRank[] };
  }>(gqlDashboard.queries.GET_ORGANIZATIONAL_RANKING);
  const { t } = useTranslation("common");

  if (loadingRanking) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <HomeLoadingStateV2 />
        <p className="mt-4 text-sm text-slate-600 dark:text-zinc-400">
          {t("Cargando ranking")}
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
          {t("Personas y equipos")}
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">
          {t("Indicadores de tus equipos")}
        </p>
      </div>
      <div className="space-y-3">
        {organizationalData?.organizationalRanking?.teamRank?.map((team) => (
          <TeamRankingCardV2 key={team.teamName} data={team} />
        ))}
      </div>
    </section>
  );
}
